import { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useGame } from '../../contexts/GameContext'
import './Profile.css'

export default function Profile({ onBack, onViewReplay }) {
  const { user, session, isGuest, triggerRegistration, signIn, updatePassword, resendVerificationEmail } = useAuth()
  const { getProfile, getMatchHistory, getRatingHistory } = useGame()

  // Create API helper using same pattern as GameContext
  const apiCall = async (endpoint, options = {}) => {
    const url = `https://riftchess-backend.fly.dev/api/v1${endpoint}`
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': session?.access_token ? `Bearer ${session.access_token}` : ''
      },
      ...options
    }

    if (config.body && typeof config.body !== 'string') {
      config.body = JSON.stringify(config.body)
    }

    const response = await fetch(url, config)

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP error! status: ${response.status}, response: ${errorText}`)
    }

    return await response.json()
  }
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [matchHistory, setMatchHistory] = useState([])
  const [ratingHistory, setRatingHistory] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [subscription, setSubscription] = useState(null)
  const [loadingSubscription, setLoadingSubscription] = useState(false)
  const [subscriptionAction, setSubscriptionAction] = useState('')
  
  // Password Change State
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordStatus, setPasswordStatus] = useState('idle') // idle, loading, success, error
  const [passwordMessage, setPasswordMessage] = useState('')

  // Verification State
  const [verificationStatus, setVerificationStatus] = useState('idle') // idle, sending, sent, error

  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    try {
      setLoading(true)
      setError(null)
      const profileData = await getProfile()
      console.log('Profile data loaded successfully:', {
        profileData,
        session,
        accountType: profileData?.account_type,
        sessionToken: session?.access_token?.substring(0, 50) + '...'
      })
      setProfile(profileData)

      // Load history data after profile loads
      loadHistoryData()
      // Load subscription data
      loadSubscriptionData()
    } catch (err) {
      console.error('Failed to load profile:', err)
      setError('Failed to load profile data')
    } finally {
      setLoading(false)
    }
  }

  const loadHistoryData = async () => {
    try {
      setLoadingHistory(true)
      const [matches, ratings] = await Promise.all([
        getMatchHistory(10), // Get last 10 matches
        getRatingHistory(50)  // Get last 50 rating changes
      ])
      setMatchHistory(matches)
      setRatingHistory(ratings)
    } catch (err) {
      console.error('Failed to load history:', err)
      // Don't set main error, just log it as history is supplementary
    } finally {
      setLoadingHistory(false)
    }
  }


  const loadSubscriptionData = async () => {
    try {
      setLoadingSubscription(true)
      const token = session?.access_token
      console.log('Loading subscription with token:', token ? 'Token exists' : 'No token')

      if (!token) {
        console.log('No access token available')
        setSubscription(null)
        return
      }

      const data = await apiCall('/billing/subscription')
      console.log('Subscription data:', data)

      if (data.has_subscription && data.subscription) {
        setSubscription(data.subscription)
      } else {
        setSubscription(null)
      }
    } catch (err) {
      console.error('Failed to load subscription data:', err)
      setSubscription(null)
    } finally {
      setLoadingSubscription(false)
    }
  }

  const handleCancelSubscription = async () => {
    if (!subscription?.stripe_subscription_id || subscriptionAction) return

    try {
      setSubscriptionAction('canceling')
      await apiCall('/billing/subscription', { method: 'DELETE' })
      await loadSubscriptionData()
    } catch (err) {
      console.error('Failed to cancel subscription:', err)
      setError('Failed to cancel subscription')
    } finally {
      setSubscriptionAction('')
    }
  }

  const handleReactivateSubscription = async () => {
    if (!subscription?.stripe_subscription_id || subscriptionAction) return

    try {
      setSubscriptionAction('reactivating')
      await apiCall('/billing/subscription/reactivate', { method: 'POST' })
      await loadSubscriptionData()
    } catch (err) {
      console.error('Failed to reactivate subscription:', err)
      setError('Failed to reactivate subscription')
    } finally {
      setSubscriptionAction('')
    }
  }

  const handleVerifyEmail = async () => {
    try {
      setVerificationStatus('sending')
      const { error } = await resendVerificationEmail()
      if (error) throw new Error(error)
      setVerificationStatus('sent')
      if (window.showToast) window.showToast('Verification email sent! Check your inbox.', 'success')
    } catch (err) {
      setVerificationStatus('error')
      if (window.showToast) window.showToast(err.message || 'Failed to send verification email', 'error')
    }
  }

  const handleUpdatePassword = async (e) => {
    e.preventDefault()
    if (newPassword !== confirmPassword) {
      setPasswordStatus('error')
      setPasswordMessage('New passwords do not match')
      return
    }
    if (newPassword.length < 6) {
      setPasswordStatus('error')
      setPasswordMessage('Password must be at least 6 characters')
      return
    }

    try {
      setPasswordStatus('loading')
      
      // 1. Verify current password by signing in
      const { error: signInError } = await signIn(user.email, currentPassword)
      if (signInError) {
        throw new Error('Incorrect current password')
      }

      // 2. Update to new password
      const { error: updateError } = await updatePassword(newPassword)
      if (updateError) {
        throw new Error(updateError.message)
      }

      setPasswordStatus('success')
      setPasswordMessage('Password updated successfully')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      setPasswordStatus('error')
      setPasswordMessage(err.message)
    }
  }

  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-loading">
          <div className="loading-spinner">Loading profile...</div>
        </div>
      </div>
    )
  }

  if (error && !profile) {
    return (
      <div className="profile-container">
        <div className="profile-header">
          <h1>Profile</h1>
        </div>
        <div className="profile-error">
          <p>{error}</p>
          <button className="retry-button" onClick={loadProfile}>
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (isGuest) {
    return (
      <div className="game-preferences">
        <div className="guest-preferences-notice">
          <div className="guest-message">
            <h3>Create an Account</h3>
            <p>Sign up to save your theme preferences and access them from any device.</p>
            <div className="account-actions">
              <button onClick={triggerRegistration} className="create-account-button">
                Create Account
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const stats = profile ? {
    rating: profile.rating || 1200,
    gamesPlayed: profile.games_played || 0,
    gamesWon: profile.games_won || 0,
    winRate: profile.games_played > 0 ? Math.round((profile.games_won / profile.games_played) * 100) : 0
  } : null

  return (
    <div className="profile-container">

      {error && (
        <div className="profile-error-banner">
          {error}
        </div>
      )}

      <div className="profile-content">
        {/* Profile Header with Avatar */}
        <div className="profile-header">
          <div className="profile-avatar-section">
            <div className="profile-avatar">
              <img
                src="/images/logo/finallogosquare.png"
                alt={`${profile?.username || 'Your'} Avatar`}
                className="profile-image"
              />
            </div>
            <div className="profile-info">
              <h1>
                {profile?.username || 'Your Profile'}
                {(user?.account_type === 'premium' || profile?.account_type === 'premium') &&
                  <span className="premium-star">★</span>}
              </h1>
              <div className="profile-subtitle">Private Profile</div>
            </div>
          </div>
        </div>

        {/* Top Row: Rating and Game Statistics */}
        <div className="top-row">
          {/* ELO Rating Section */}
          <div className="profile-card rating-card">
            <h2>Rating</h2>
            <div className="rating-display">
              <div className="rating-number">
                {stats?.rating || 1200}
              </div>
              <div className="rating-label">ELO Rating</div>
            </div>
            <div className="rating-description">
              Your current chess rating based on game performance
            </div>
          </div>

          {/* Game Statistics Section */}
          <div className="profile-card stats-card">
            <h2>Game Statistics</h2>

            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-number">{stats?.gamesPlayed || 0}</div>
                <div className="stat-label">Games Played</div>
              </div>

              <div className="stat-item">
                <div className="stat-number">{stats?.gamesWon || 0}</div>
                <div className="stat-label">Games Won</div>
              </div>

              <div className="stat-item">
                <div className="stat-number">{stats?.winRate || 0}%</div>
                <div className="stat-label">Win Rate</div>
              </div>

              <div className="stat-item">
                <div className="stat-number">{(stats?.gamesPlayed || 0) - (stats?.gamesWon || 0)}</div>
                <div className="stat-label">Games Lost</div>
              </div>
            </div>

            {stats?.gamesPlayed === 0 && (
              <div className="no-games-message">
                <p>No games played yet. Start playing to see your statistics!</p>
              </div>
            )}
          </div>
        </div>

        {/* User Info Section - Full Width Below */}
        <div className="profile-card user-info-card">
          <h2>User Information</h2>

          <div className="user-info-section">
            <label>Email</label>
            <div className="user-info-value">
              {user?.email || 'Unknown'}
            </div>
          </div>

          <div className="user-info-section">
            <label>Username</label>
            <div className="user-info-value">
              {profile?.username || 'No username set'}
            </div>
          </div>

          <div className="user-info-section">
            <label>Member Since</label>
            <div className="user-info-value">
              {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'Unknown'}
            </div>
          </div>

          <div className="user-info-section">
            <label>Status</label>
            <div className="user-info-value" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {profile?.email_verified ? (
                  <>
                    <span style={{ color: '#22c55e' }}>●</span>
                    <span>Verified</span>
                  </>
                ) : (
                  <>
                    <span style={{ color: '#ef4444' }}>●</span>
                    <span>Unverified</span>
                  </>
                )}
              </div>
              {!profile?.email_verified && (
                <button 
                  onClick={handleVerifyEmail}
                  disabled={verificationStatus === 'sending' || verificationStatus === 'sent'}
                  className="edit-button"
                  style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem', marginLeft: '1rem' }}
                >
                  {verificationStatus === 'sending' ? 'Sending...' : (verificationStatus === 'sent' ? 'Sent!' : 'Verify Email')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Change Password Section */}
        <div className="profile-card password-card">
          <h2>Change Password</h2>
          <form onSubmit={handleUpdatePassword} className="password-form">
            <div className="user-info-section">
              <label>Current Password</label>
              <div className="password-input-wrapper" style={{ position: 'relative' }}>
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="username-input"
                  style={{ width: '100%' }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer'
                  }}
                >
                  {showCurrentPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  )}
                </button>
              </div>
            </div>

            <div className="user-info-section">
              <label>New Password</label>
              <div className="password-input-wrapper" style={{ position: 'relative' }}>
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="username-input"
                  style={{ width: '100%' }}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer'
                  }}
                >
                  {showNewPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  )}
                </button>
              </div>
            </div>

            <div className="user-info-section">
              <label>Confirm New Password</label>
              <div className="password-input-wrapper" style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="username-input"
                  style={{ width: '100%' }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer'
                  }}
                >
                  {showConfirmPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  )}
                </button>
              </div>
            </div>

            <div className="password-actions" style={{ marginTop: '1.5rem' }}>
              <button 
                type="submit" 
                className="save-button"
                disabled={passwordStatus === 'loading'}
              >
                {passwordStatus === 'loading' ? 'Updating...' : 'Update Password'}
              </button>
            </div>

            {passwordMessage && (
              <div style={{ 
                marginTop: '1rem', 
                padding: '0.75rem', 
                borderRadius: '4px',
                backgroundColor: passwordStatus === 'error' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                color: passwordStatus === 'error' ? '#ef4444' : '#22c55e',
                fontSize: '0.9rem'
              }}>
                {passwordMessage}
              </div>
            )}
          </form>
        </div>


        {/* Subscription Management Section - Static for now since API is not working */}
        {(user?.account_type === 'premium' || profile?.account_type === 'premium') && (
          <div className="profile-card subscription-card">
            <h2>Subscription Management</h2>
            {loadingSubscription ? (
              <div className="subscription-loading">Loading subscription details...</div>
            ) : subscription ? (
              <div className="subscription-details">
                <div className="subscription-info">
                  <div className="subscription-status">
                    <span className={`status-badge ${subscription.status}`}>
                      {subscription.status === 'active' ? 'Active' : subscription.status}
                    </span>
                    <span className="plan-type">{subscription.plan_id} Plan</span>
                  </div>

                  <div className="subscription-period">
                    <div className="period-info">
                      <label>Current Period:</label>
                      <span>
                        {subscription.current_period_start && subscription.current_period_end ?
                          `${new Date(subscription.current_period_start).toLocaleDateString()} - ${new Date(subscription.current_period_end).toLocaleDateString()}` :
                          'Unknown'
                        }
                      </span>
                    </div>

                    {subscription.cancel_at_period_end && (
                      <div className="cancellation-notice">
                        <span className="warning-text">
                          ⚠️ Your subscription will not renew after {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : 'the current period'}
                        </span>
                      </div>
                    )}

                    {subscription.grace_period_end && (
                      <div className="grace-period-notice">
                        <span className="warning-text">
                          ⏰ Grace period ends: {new Date(subscription.grace_period_end).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="subscription-actions">
                  {subscription.cancel_at_period_end ? (
                    <button
                      className="reactivate-button"
                      onClick={handleReactivateSubscription}
                      disabled={subscriptionAction === 'reactivating'}
                    >
                      {subscriptionAction === 'reactivating' ? 'Reactivating...' : 'Reactivate Subscription'}
                    </button>
                  ) : (
                    <button
                      className="cancel-button"
                      onClick={handleCancelSubscription}
                      disabled={subscriptionAction === 'canceling'}
                    >
                      {subscriptionAction === 'canceling' ? 'Canceling...' : 'Cancel Subscription'}
                    </button>
                  )}

                  <div className="subscription-note">
                    <p>
                      {subscription.cancel_at_period_end ?
                        'Reactivate your subscription to continue receiving premium benefits.' :
                        'Canceling will stop auto-renewal at the end of your current billing period.'
                      }
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="subscription-not-found">
                <p><strong>Premium Account Active</strong></p>
                <p>Your premium benefits are active, but subscription details are not available.</p>
                <button onClick={loadSubscriptionData} className="retry-button" style={{marginTop: '1rem'}}>
                  Refresh Subscription Data
                </button>
              </div>
            )}
          </div>
        )}

        {/* Rating History Chart */}
        <div className="profile-card rating-history-card">
          <h2>Rating History</h2>
          {loadingHistory ? (
            <div className="history-loading">Loading rating history...</div>
          ) : ratingHistory.length > 0 ? (
            <div className="rating-chart">
              <div className="rating-chart-container">
                {ratingHistory.slice(-20).map((entry, index) => {
                  const height = Math.max(10, (entry.rating_after - 1000) / 10)
                  const isPositive = entry.rating_change > 0
                  return (
                    <div key={entry.game_id} className="rating-bar">
                      <div
                        className={`rating-point ${isPositive ? 'positive' : 'negative'}`}
                        style={{ height: `${height}px` }}
                        title={`${entry.rating_after} (${isPositive ? '+' : ''}${entry.rating_change}) - ${entry.result}`}
                      />
                    </div>
                  )
                })}
              </div>
              <div className="rating-chart-info">
                <p>Last 20 rated games showing rating changes</p>
                <div className="rating-legend">
                  <span className="legend-item positive">Win/Draw with gain</span>
                  <span className="legend-item negative">Loss or rating decrease</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-rating-history">
              <p>No rating history available. Play some rated games to see your progress!</p>
            </div>
          )}
        </div>

        {/* Match History */}
        <div className="profile-card match-history-card">
          <h2>Recent Games</h2>
          {loadingHistory ? (
            <div className="history-loading">Loading match history...</div>
          ) : matchHistory.length > 0 ? (
            <div className="match-history-list">
              {matchHistory.slice(0, 10).map((match) => {
                const isWhite = match.white_player_id === user?.id
                const opponent = isWhite ? match.black_player : match.white_player
                const playerRatingBefore = isWhite ? match.white_rating_before : match.black_rating_before
                const playerRatingAfter = isWhite ? match.white_rating_after : match.black_rating_after
                const ratingChange = playerRatingAfter - playerRatingBefore

                let result = 'Draw'
                let resultClass = 'draw'
                if (match.result === 'white_win') {
                  result = isWhite ? 'Won' : 'Lost'
                  resultClass = isWhite ? 'win' : 'loss'
                } else if (match.result === 'black_win') {
                  result = isWhite ? 'Lost' : 'Won'
                  resultClass = isWhite ? 'loss' : 'win'
                }

                return (
                  <div key={match.id} className="match-history-item">
                    <div className="match-info">
                      <div className="match-result">
                        <span className={`result-badge ${resultClass}`}>{result}</span>
                      </div>
                      <div className="match-details">
                        <div className="opponent">
                          vs {opponent?.username || 'Anonymous'}
                        </div>
                        <div className="match-meta">
                          {isWhite ? 'White' : 'Black'} • {new Date(match.ended_at || match.updated_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="match-rating">
                      <div
                        className={`rating-change ${ratingChange < 0 ? 'negative' : 'positive'}`}
                      >
                        {ratingChange > 0 ? '+' : ''}{ratingChange}
                      </div>
                      <div className="rating-value">
                        {playerRatingAfter}
                      </div>
                    </div>
                    {onViewReplay && match.status === 'completed' && (
                      <button
                        className="replay-button"
                        onClick={() => onViewReplay(match.id)}
                        title="View game replay"
                      >
                        ▶
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="no-match-history">
              <p>No game history available. Play some games to see your match history!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
